// src/services/authCodes.js
//
// Email codes for signup and SMS codes for phone verification, through the
// auth-codes Edge Function — the same one the app uses. The server hashes
// codes, caps attempts, rate-limits sends, and is the only thing that can mark
// a phone verified.
import supabase from './supabase';

async function call(action, payload) {
  const { data, error } = await supabase.functions.invoke('auth-codes', {
    body: { action, ...payload },
  });

  if (error) {
    let message = 'Something went wrong. Please try again.';
    let code;
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
      code = body?.code;
    } catch {
      // Keep the generic message.
    }
    const err = new Error(message);
    err.code = code;
    throw err;
  }
  return data;
}

export const sendSignupEmailCode = (email) => call('signup_email_send', { email });

export const verifySignupEmailCode = async (email, code) =>
  (await call('signup_email_verify', { email, code })).signup_token;

export const completeEmailSignup = ({ email, signupToken, username, password }) =>
  call('signup_complete', { email, signup_token: signupToken, username, password });

export const sendPhoneCode = (phone) => call('phone_send', { phone });

export const verifyPhoneCode = (phone, code) => call('phone_verify', { phone, code });

// ── Signup with a phone ─────────────────────────────────────────────────────
export const sendSignupPhoneCode = (phone) => call('signup_phone_send', { phone });

export const verifySignupPhoneCode = async (phone, code) =>
  (await call('signup_phone_verify', { phone, code })).signup_token;

export const completePhoneSignup = ({ phone, signupToken, username, password }) =>
  call('signup_complete', { phone, signup_token: signupToken, username, password });

/**
 * Sign in with phone + password: checked by the server, which returns a
 * one-time token that becomes the session here. The account's auth email is
 * never exposed to the page.
 */
export async function signInWithPhone(phone, password) {
  const { token_hash: tokenHash } = await call('phone_login', { phone, password });
  const { data, error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'magiclink' });
  if (error) throw new Error('Signing in failed. Please try again.');
  return data;
}

// ── Adding an email to a signed-in account (the publish gate) ───────────────
export const sendEmailCode = (email) => call('email_send', { email });
export const verifyEmailCode = (email, code) => call('email_verify', { email, code });

/** After a Google / Apple sign-in: makes sure the Invits profile row exists. */
export const completeOAuthSignIn = () => call('oauth_complete', {});
